/**
 * File: useADRUpdateListener.ts
 * Description: Custom hook to handle ADR update events for refreshing timestamps
 * Author: GitHub Copilot
 * Created: 2025-01-XX
 * Last Modified: 2025-01-XX
 */

import { useEffect } from 'react';

import { adrService } from '../../services/adrService';
import { onAdrUpdated } from '../../services/adrUpdateEvents';
import { ADRDefinition } from '../../types/adr';

interface UseADRUpdateListenerProps {
  projectId: string | undefined;
  onDefinitionsUpdated: (_definitions: ADRDefinition[]) => void;
}

/**
 * Hook to listen for ADR update events and refresh definitions
 */
export const useADRUpdateListener = ({
  projectId,
  onDefinitionsUpdated,
}: UseADRUpdateListenerProps): void => {
  useEffect(() => {
    if (!projectId) {
      return undefined;
    }

    const unsubscribe = onAdrUpdated((eventProjectId: number) => {
      // Verify it's for this project
      if (eventProjectId === Number.parseInt(projectId, 10)) {
        // Reload the specific ADR definition to get updated timestamp
        const refreshDefinition = async () => {
          try {
            const updatedDefinitions = await adrService.getADRDefinitions(Number.parseInt(projectId, 10));
            onDefinitionsUpdated(updatedDefinitions);
          } catch {
            // Silently fail - stale data is acceptable
          }
        };
        refreshDefinition();
      }
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  // Note: onDefinitionsUpdated intentionally excluded from deps as it's a stable setState function
};
